/**
 * The product's icon set — design-fidelity audit T-1.2 / gap F-2.
 *
 * Both apps hardcoded Unicode dingbats (`◱ ⇄ ◎ ⋈ ▶ ⇥ ▤ ⚙`) in place of icons.
 * Those have inconsistent optical weight, width and baseline across fonts, they
 * cannot inherit a stroke weight, and several render as emoji on some platforms —
 * so the nav read as a row of mismatched symbols next to the prototype's coherent
 * line set.
 *
 * **These paths are the prototype's own**, lifted verbatim from its `navMeta()`
 * table (see docs/design-system/prototype/pages/main.html), not approximated from
 * an icon library. That was the deciding factor over adopting Lucide, which the
 * audit had proposed as D-2: the design's icons already existed as authored path
 * data, so copying them is exact where a library match would be "close", and it
 * adds no dependency and no licence question to a set we already own.
 *
 * The house style, which any *new* icon must follow:
 *   - 24×24 viewBox
 *   - `fill: none`, `stroke: currentColor`, `stroke-width: 1.7`
 *   - round caps and joins
 *   - drawn at 19–20px in the nav, 16–18px inline
 *
 * Lucide remains the right source for icons the prototype never drew (its default
 * geometry is the same 24-grid), but it should be brought in behind this same
 * `Icon` component rather than imported at call sites, so the stroke weight and
 * sizing stay in one place.
 */

export type IconName =
  | 'dashboard'
  | 'sources'
  | 'findings'
  | 'matches'
  | 'actions'
  | 'destinations'
  | 'reports'
  | 'settings'
  | 'help'
  | 'search'
  | 'bell'
  | 'menu'
  | 'close'
  | 'chevronDown'
  | 'chevronRight'
  | 'check'
  | 'external'
  // --- F30's operator console (admin.dedupr.cloud) ---
  | 'tenants'
  | 'subscription'
  | 'provisioning'
  | 'fleet'
  | 'audit'
  // --- F26's settings surface (settings.dedupr.cloud) ---
  | 'credentials'
  | 'members'
  | 'appearance';

/**
 * Path data per icon. A single `d` string each, exactly as the prototype
 * authored them — multi-subpath icons use `M` breaks rather than separate
 * elements, which is why these are strings and not arrays.
 */
export const ICON_PATHS: Record<IconName, string> = {
  // --- The eight primary-nav icons, verbatim from the prototype ---
  dashboard: 'M4 4h7v7H4zM13 4h7v5h-7zM13 11h7v9h-7zM4 13h7v7H4z',
  sources:
    'M12 3.5c4.1 0 7.5 1.2 7.5 2.7S16.1 8.9 12 8.9 4.5 7.7 4.5 6.2 7.9 3.5 12 3.5zM4.5 6.2v11.6c0 1.5 3.4 2.7 7.5 2.7s7.5-1.2 7.5-2.7V6.2M4.5 12c0 1.5 3.4 2.7 7.5 2.7s7.5-1.2 7.5-2.7',
  findings: 'M11 4.2a6.8 6.8 0 1 1 0 13.6 6.8 6.8 0 0 1 0-13.6zM20 20l-4.2-4.2',
  matches: 'M4 8.5h13M14 5.5l3 3-3 3M20 15.5H7M10 12.5l-3 3 3 3',
  actions: 'M12 3.2a8.8 8.8 0 1 1 0 17.6 8.8 8.8 0 0 1 0-17.6zM10.2 8.5l5.6 3.5-5.6 3.5z',
  destinations:
    'M12 15.5V4.5M8.2 8.3 12 4.5l3.8 3.8M4.5 15.5v3a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-3',
  reports: 'M4 20h16M7.5 20v-6.5M12 20V6M16.5 20v-9',

  // --- Drawn in the same house style for the slots the prototype rendered with
  //     glyphs or divs (settings gear, the 3-div hamburger, the ⌕/✕/▾ glyphs).
  //     Marked as ours rather than the design's, so a future designer pass knows
  //     which of these to review.
  settings:
    'M12 9.2a2.8 2.8 0 1 1 0 5.6 2.8 2.8 0 0 1 0-5.6zM12 3.5l1 2.2 2.4-.5 1 2-1.6 1.9.9 2.3 2.3.6v2l-2.3.6-.9 2.3 1.6 1.9-1 2-2.4-.5-1 2.2-1-2.2-2.4.5-1-2 1.6-1.9-.9-2.3-2.3-.6v-2l2.3-.6.9-2.3L7.6 5.2l1-2 2.4.5z',
  // The prototype's bell, verbatim (its one other inline SVG).
  bell: 'M18 8.5a6 6 0 1 0-12 0c0 4.2-1.6 5.5-2 6.5h16c-.4-1-2-2.3-2-6.5M10 19a2 2 0 0 0 4 0',
  help: 'M12 3.5a8.5 8.5 0 1 1 0 17 8.5 8.5 0 0 1 0-17zM9.6 9.3a2.5 2.5 0 0 1 4.9.7c0 1.7-2.5 2-2.5 3.6M12 17h.01',
  search: 'M11 4.2a6.8 6.8 0 1 1 0 13.6 6.8 6.8 0 0 1 0-13.6zM20 20l-4.2-4.2',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6L6 18',
  chevronDown: 'M6 9.5l6 6 6-6',
  chevronRight: 'M9.5 6l6 6-6 6',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  external: 'M14 4h6v6M20 4l-8.5 8.5M17 14v4.5A1.5 1.5 0 0 1 15.5 20h-10A1.5 1.5 0 0 1 4 18.5v-10A1.5 1.5 0 0 1 5.5 7H10',

  // --- F30's operator console. Ours, not the design's: the prototype covers the
  //     18 customer pages and never drew an internal console, so these five are
  //     authored in the house style above (24-grid, 1.7 stroke, round joins) and
  //     are the ones a future designer pass should review first.
  //
  //     Each one deliberately avoids resembling a customer-facing icon it sits
  //     beside in nobody's nav but could be confused with in a screenshot:
  //     `tenants` is a building, not `dashboard`'s panels; `fleet` is a grid of
  //     sites, not `reports`' bars.

  // A building — an organisation, which is what a tenant is.
  tenants: 'M5 20V6.5A1.5 1.5 0 0 1 6.5 5h7A1.5 1.5 0 0 1 15 6.5V20M15 11h2.5A1.5 1.5 0 0 1 19 12.5V20M3.5 20h17M8 9h4M8 12.5h4M8 16h4',
  // A card with a stripe — a subscription, not a generic document.
  subscription: 'M3.5 7.5A1.5 1.5 0 0 1 5 6h14a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19 18H5a1.5 1.5 0 0 1-1.5-1.5zM3.5 10.5h17M6.5 14.5h4',
  // Stacked layers with an arrow into them — infrastructure being provisioned.
  provisioning: 'M4.5 8.5 12 5l7.5 3.5L12 12zM4.5 12.5 12 16l7.5-3.5M4.5 16.5 12 20l7.5-3.5',
  // A grid of sites with a pulse through it — many tenants, watched at once.
  fleet: 'M4 5.5h6v6H4zM14 5.5h6v6h-6zM4 15h6v4.5H4zM14 15h6v4.5h-6',
  // A list with a tick — an append-only trail of what happened.
  audit: 'M6.5 4h11A1.5 1.5 0 0 1 19 5.5v13A1.5 1.5 0 0 1 17.5 20h-11A1.5 1.5 0 0 1 5 18.5v-13A1.5 1.5 0 0 1 6.5 4zM8.5 8.5h7M8.5 12h7M8.5 15.5h3.5',

  // --- `settings.dedupr.cloud`. Also ours, and added for the same reason as the
  //     operator set: these screens were reusing tenant-app icon names, so the
  //     COLLAPSED rail — where the icon is the only affordance left — showed a
  //     database for "Plan & subscription", a play button for "Credentials", an
  //     upload arrow for "Members & roles" and a bar chart for "Appearance". The
  //     labels hid it while expanded; collapsed, the nav was unreadable.

  // A key — a credential.
  credentials: 'M15.5 4a4.5 4.5 0 1 1-4.37 5.56L4 16.7V20h3.3l.9-.9v-1.9h1.9l.9-.9v-1.6l1.6-1.6M16.6 8.9h.01',
  // Two people — members.
  members: 'M9.2 4.5a3.3 3.3 0 1 1 0 6.6 3.3 3.3 0 0 1 0-6.6zM3.5 19.5v-1.2a4.2 4.2 0 0 1 4.2-4.2h3a4.2 4.2 0 0 1 4.2 4.2v1.2M16.2 5.1a3.3 3.3 0 0 1 0 6.4M17.6 14.2a4.2 4.2 0 0 1 2.9 4v1.3',
  // A circle split light/dark — contrast, i.e. theme and appearance.
  appearance: 'M12 3.5a8.5 8.5 0 1 1 0 17 8.5 8.5 0 0 1 0-17zM12 3.5v17',
};

/** The house stroke weight. One value, so no icon can drift off it. */
export const ICON_STROKE_WIDTH = 1.7;
export const ICON_VIEW_BOX = '0 0 24 24';

/**
 * The mono glyph column used in MENU ROWS — the avatar menu and org menu lead
 * each row with a small IBM Plex Mono character in an 18px slot, verbatim from
 * the prototype's `avatarSections` table. This is a *different, deliberate*
 * treatment from the nav's stroke SVGs: the prototype draws primary navigation
 * and renders menu rows with type. Reproducing that distinction is part of
 * fidelity; replacing these with drawn icons would erase it.
 *
 * This file is glyph-lint-exempt by name, which is exactly where a sanctioned
 * glyph list belongs — a call site never types a raw dingbat, it references
 * `MENU_GLYPHS.profile`.
 */
export type MenuGlyphName =
  | 'profile'
  | 'mail'
  | 'keyboard'
  | 'org'
  | 'safety'
  | 'ai'
  | 'people'
  | 'audit'
  | 'billing'
  | 'logout';

/**
 * The glyph column used in CALLOUTS — same rationale as `MENU_GLYPHS`, different
 * vocabulary. The prototype leads a tinted callout with a typeset mono character
 * rather than a drawn icon, and the set it draws from is small and meaningful:
 *
 *   stop      a safeguard the user must not walk past casually
 *   warn      something is off but recoverable
 *   info      an explanation of how the screen works
 *   retry     a value was recomputed, or a job is backing off
 *   ai        an AI-attributed statement (matches the AI token group)
 *   money     a currency figure or savings estimate
 *   ok        a check that passed
 *
 * Sanctioned here rather than typed at each call site so `lint:tokens`'s
 * glyph-icon rule keeps working: a component that accepts an arbitrary string
 * would let the next screen paste a dingbat straight into JSX, which is the exact
 * drift the rule exists to catch. Callers reference `CALLOUT_GLYPHS.stop`.
 */
export type CalloutGlyphName = 'stop' | 'warn' | 'info' | 'retry' | 'ai' | 'money' | 'ok';

export const CALLOUT_GLYPHS: Record<CalloutGlyphName, string> = {
  stop: '⛔', // ⛔
  warn: '⚠', // ⚠
  info: '◉', // ◉
  retry: '↻', // ↻
  ai: '✻', // ✻
  money: '€', // €
  ok: '✓', // ✓
};

/**
 * The verdict marks used by `MarkList` — ✓ / ✕ / ! / · / € / −.
 *
 * Third sanctioned glyph table, same rationale as the other two: the design typesets
 * these rather than drawing them, and keeping the characters in this one exempt file
 * means no call site ever types a dingbat.
 *
 * It is here for a second reason worth recording: the table originally lived in
 * `MarkList.tsx` and **`lint:tokens` did not flag it**, because the glyph rule is
 * position-scoped (it looks for an `icon:`/`glyph:` key, an `aria-hidden` line, or a
 * bare short string) and `yes: '✓',` matches none of those. Passing by accident is not
 * the same as being sanctioned, and a rule that silently permits the thing it exists to
 * catch is worth closing by construction rather than by widening the regex.
 */
export type MarkGlyphName = 'yes' | 'no' | 'warn' | 'neutral' | 'money' | 'minus';

export const MARK_GLYPHS: Record<MarkGlyphName, string> = {
  yes: '✓',
  no: '✕',
  warn: '!',
  neutral: '·',
  money: '€',
  minus: '−',
};

/**
 * ⚠️ **Written as literal characters, deliberately — they were `\uXXXX` escapes until
 * 2026-08-08, and that mattered ([B-97](../../../docs/platform/TODO.md)).**
 *
 * `profile` is U+25CE `◎`, one of the four nav dingbats (`◱ ⇄ ◎ ⋈`) the glyph rule was written to
 * ban. Here it is correct — it is the prototype's own `avatarSections` character and this file is
 * the sanctioned home for such a table. What was **not** correct is that no character class can
 * match an escape sequence, so the same glyph in any other file would have passed too: the rule
 * was one keystroke away from being evadable everywhere, and the evasion was sitting in the table
 * that proves it works. `lint:tokens` now decodes escapes before scanning, and these are spelled
 * the way the other two tables spell theirs so the file reads as one thing.
 */
export const MENU_GLYPHS: Record<MenuGlyphName, string> = {
  profile: '◎',
  mail: '✉',
  keyboard: '⌨',
  org: '⌂',
  safety: '⛨',
  ai: '✻',
  people: '☰',
  audit: '▤',
  billing: '€',
  logout: '⏻',
};

/**
 * The two disclosure markers — the fourth sanctioned table.
 *
 * `DataTable`'s expandable row is a **text triangle**, not a rotating chevron: measured from the
 * prototype and recorded in [components.md](../../../docs/design-system/components.md)'s
 * `DataTable` row ("text `▲`/`▼`"). It lived inline as `{isOpen ? '▲' : '▼'}` and passed the glyph
 * rule for the same reason `MARK_GLYPHS` did before it moved here — a ternary of one-character
 * literals matched none of the rule's icon positions. Both are sanctioned by name now, and caught
 * by rule anywhere else.
 */
export type DisclosureGlyphName = 'open' | 'closed';

export const DISCLOSURE_GLYPHS: Record<DisclosureGlyphName, string> = {
  open: '▲',
  closed: '▼',
};

/**
 * The em dash `ChoiceTile` pairs with `MARK_GLYPHS.yes` in its point list.
 *
 * Named rather than typed inline so the choice is visible: it is **not** `MARK_GLYPHS.minus`
 * (U+2212 MINUS SIGN). components.md records the prototype's point list as `✓/—`, and an em dash
 * reading "not included" is a different statement from a minus sign reading "subtract". The two
 * are near-identical on screen, which is exactly why swapping one for the other would never have
 * been noticed — and why "close enough" is the failure mode the fidelity audit exists to stop.
 */
export const EM_DASH = '—';
